import { useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Clock, LogIn, LogOut, Check, X, Pencil, Plus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DeleteButton } from "@/components/DeleteButton";

function fmt(iso) { return iso ? format(new Date(iso), "MMM d, h:mm a") : "—"; }
function toLocalInput(iso) { return iso ? format(new Date(iso), "yyyy-MM-dd'T'HH:mm") : ""; }
function hours(a, b) {
  if (!a || !b) return "—";
  const h = (new Date(b) - new Date(a)) / 3.6e6;
  return `${h.toFixed(2)} hrs`;
}
const STATUS = {
  active: { label: "Clocked in", cls: "bg-blue-500" },
  pending: { label: "Pending approval", cls: "bg-orange-500" },
  approved: { label: "Approved", cls: "bg-primary" },
  rejected: { label: "Rejected", cls: "bg-destructive" },
};
function StatusBadge({ s }) { const m = STATUS[s] || STATUS.pending; return <Badge className={m.cls}>{m.label}</Badge>; }

export default function TimeClock() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [active, setActive] = useState(null);
  const [mine, setMine] = useState([]);
  const [all, setAll] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [editForm, setEditForm] = useState({ clock_in: "", clock_out: "" });
  const [workers, setWorkers] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ worker_id: "", clock_in: "", clock_out: "", status: "approved" });
  const [logFilter, setLogFilter] = useState({ worker_id: "all", status: "all", start: "", end: "" });

  useEffect(() => { load(); }, []);
  useEffect(() => { if (isAdmin) loadLog(); /* eslint-disable-next-line */ }, [logFilter]);
  async function load() {
    try {
      const [st, my] = await Promise.all([api.get("/staff/clock-status"), api.get("/staff/time-entries/my")]);
      setActive(st.data.active); setMine(my.data);
      if (isAdmin) {
        const w = await api.get("/staff/workers"); setWorkers(w.data);
        await loadLog();
      }
    } catch { /* */ }
  }

  async function loadLog(f = logFilter) {
    try {
      const params = {};
      if (f.worker_id && f.worker_id !== "all") params.worker_id = f.worker_id;
      if (f.status && f.status !== "all") params.status = f.status;
      if (f.start && f.end) { params.start = f.start; params.end = f.end; }
      const { data } = await api.get("/staff/time-entries", { params });
      setAll(data);
    } catch { /* */ }
  }

  async function toggle() {
    setBusy(true);
    try {
      if (active) { await api.post("/staff/clock-out"); toast.success("Clocked out. Entry sent for approval."); }
      else { await api.post("/staff/clock-in"); toast.success("Clocked in. Have a great shift!"); }
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
    finally { setBusy(false); }
  }

  async function decide(id, approved) {
    try {
      await api.patch(`/staff/time-entries/${id}/decision`, { approved });
      toast.success(approved ? "Shift approved." : "Shift rejected.");
      load();
    } catch { toast.error("Failed."); }
  }

  function openEdit(e) {
    setEditEntry(e);
    setEditForm({ clock_in: toLocalInput(e.clock_in), clock_out: toLocalInput(e.clock_out) });
  }

  async function saveEdit(ev) {
    ev.preventDefault();
    if (!editForm.clock_in) return toast.error("Clock-in time is required.");
    try {
      await api.patch(`/staff/time-entries/${editEntry.id}`, {
        clock_in: editForm.clock_in, clock_out: editForm.clock_out || null,
      });
      toast.success("Times updated.");
      setEditEntry(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
  }

  function openAdd() {
    setAddForm({ worker_id: "", clock_in: "", clock_out: "", status: "approved" });
    setAddOpen(true);
  }
  async function submitAdd(ev) {
    ev.preventDefault();
    if (!addForm.worker_id || !addForm.clock_in) return toast.error("Pick a worker and a clock-in time.");
    if (addForm.clock_out && addForm.clock_out < addForm.clock_in) return toast.error("Clock-out cannot be before clock-in.");
    try {
      await api.post("/staff/time-entries", {
        worker_id: addForm.worker_id, clock_in: addForm.clock_in,
        clock_out: addForm.clock_out || null, status: addForm.status,
      });
      toast.success("Log added.");
      setAddOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
  }

  async function delEntry(id) {
    try { await api.delete(`/staff/time-entries/${id}`); toast.success("Log removed."); load(); }
    catch { toast.error("Failed to remove."); }
  }

  async function approveAllPending() {
    const wid = logFilter.worker_id !== "all" ? logFilter.worker_id : null;
    try {
      const { data } = await api.post("/staff/time-entries/approve-range", { worker_id: wid });
      toast.success(data.approved ? `Approved ${data.approved} pending shift(s).` : "No pending shifts to approve.");
      load();
    } catch { toast.error("Failed to approve."); }
  }

  const pending = all.filter((e) => e.status === "pending");
  const totalHours = all.reduce((sum, e) => {
    if (!e.clock_in || !e.clock_out) return sum;
    const h = (new Date(e.clock_out) - new Date(e.clock_in)) / 3600000;
    return sum + (h > 0 ? h : 0);
  }, 0);
  const addInvalid = !!addForm.clock_out && addForm.clock_out < addForm.clock_in;
  const editInvalid = !!editForm.clock_out && editForm.clock_out < editForm.clock_in;

  return (
    <div>
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-primary"><Clock className="h-6 w-6" /></div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Time Clock</h1>
          <p className="text-sm text-muted-foreground">Clock in and out. Shifts are logged once an admin approves them.</p>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-border bg-card p-6" data-testid="clock-panel">
        <div>
          <p className="text-sm text-muted-foreground">Status</p>
          <p className="font-heading text-xl font-bold" data-testid="clock-status">
            {active ? `Clocked in since ${fmt(active.clock_in)}` : "Not clocked in"}
          </p>
        </div>
        <Button onClick={toggle} disabled={busy} data-testid="clock-toggle"
          className={`rounded-full px-8 ${active ? "bg-destructive hover:bg-destructive/90" : ""}`}>
          {active ? <><LogOut className="mr-2 h-4 w-4" /> Clock Out</> : <><LogIn className="mr-2 h-4 w-4" /> Clock In</>}
        </Button>
      </div>

      {isAdmin && (
        <div className="mt-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-heading text-lg font-bold">Pending approvals {pending.length > 0 && <Badge className="ml-1 bg-orange-500">{pending.length}</Badge>}</h2>
            {pending.length > 0 && <Button size="sm" className="rounded-full" onClick={approveAllPending} data-testid="approve-all-pending"><Check className="mr-1 h-3 w-3" /> Approve all pending</Button>}
          </div>
          <div className="mt-3 overflow-hidden rounded-xl border border-border/60 bg-card">
            <Table>
              <TableHeader><TableRow><TableHead>Worker</TableHead><TableHead>Clock In</TableHead><TableHead>Clock Out</TableHead><TableHead>Hours</TableHead><TableHead className="text-right">Decision</TableHead></TableRow></TableHeader>
              <TableBody>
                {pending.length === 0 ? <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">No shifts awaiting approval.</TableCell></TableRow>
                  : pending.map((e) => (
                    <TableRow key={e.id} data-testid={`approve-row-${e.id}`}>
                      <TableCell className="font-medium">{e.worker_name}</TableCell>
                      <TableCell className="text-sm">{fmt(e.clock_in)}</TableCell>
                      <TableCell className="text-sm">{fmt(e.clock_out)}</TableCell>
                      <TableCell className="text-sm">{hours(e.clock_in, e.clock_out)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" className="rounded-full" onClick={() => openEdit(e)} data-testid={`approve-edit-${e.id}`}><Pencil className="mr-1 h-3 w-3" /> Edit</Button>
                          <Button size="sm" className="rounded-full" onClick={() => decide(e.id, true)} data-testid={`approve-yes-${e.id}`}><Check className="mr-1 h-3 w-3" /> Approve</Button>
                          <Button size="sm" variant="outline" className="rounded-full text-destructive" onClick={() => decide(e.id, false)} data-testid={`approve-no-${e.id}`}><X className="mr-1 h-3 w-3" /> Reject</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {isAdmin && (
        <div className="mt-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-heading text-lg font-bold">Clock in/out history</h2>
            <Button size="sm" className="rounded-full" onClick={openAdd} data-testid="add-log-open"><Plus className="mr-1 h-4 w-4" /> Add Log</Button>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">Complete clock in/out history for every worker — add, edit, or remove any entry. Approved hours feed the Pay Preview.</p>
          <div className="mt-3 flex flex-wrap items-end gap-2 rounded-xl border border-border/60 bg-card p-3">
            <div className="space-y-1">
              <Label className="text-xs">Worker</Label>
              <Select value={logFilter.worker_id} onValueChange={(v) => setLogFilter({ ...logFilter, worker_id: v })}>
                <SelectTrigger className="h-9 w-44" data-testid="log-filter-worker"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All workers</SelectItem>
                  {workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Status</Label>
              <Select value={logFilter.status} onValueChange={(v) => setLogFilter({ ...logFilter, status: v })}>
                <SelectTrigger className="h-9 w-40" data-testid="log-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="text-xs">From</Label><Input type="date" className="h-9 w-40" value={logFilter.start} onChange={(e) => setLogFilter({ ...logFilter, start: e.target.value })} data-testid="log-filter-start" /></div>
            <div className="space-y-1"><Label className="text-xs">To</Label><Input type="date" className="h-9 w-40" value={logFilter.end} onChange={(e) => setLogFilter({ ...logFilter, end: e.target.value })} data-testid="log-filter-end" /></div>
            {(logFilter.worker_id !== "all" || logFilter.status !== "all" || logFilter.start || logFilter.end) && (
              <Button variant="ghost" size="sm" className="h-9 rounded-full" onClick={() => setLogFilter({ worker_id: "all", status: "all", start: "", end: "" })} data-testid="log-filter-clear"><X className="mr-1 h-3 w-3" /> Clear</Button>
            )}
          </div>
          <p className="mt-2 text-xs font-medium text-muted-foreground" data-testid="log-summary">{all.length} {all.length === 1 ? "entry" : "entries"} · {totalHours.toFixed(2)} total hours</p>
          <div className="mt-3 overflow-hidden rounded-xl border border-border/60 bg-card">
            <Table>
              <TableHeader><TableRow><TableHead>Worker</TableHead><TableHead>Clock In</TableHead><TableHead>Clock Out</TableHead><TableHead>Hours</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {all.length === 0 ? <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">No time entries match these filters.</TableCell></TableRow>
                  : all.map((e) => (
                    <TableRow key={e.id} data-testid={`log-row-${e.id}`}>
                      <TableCell className="font-medium">{e.worker_name}{e.added_by_admin && <Badge variant="outline" className="ml-2 text-[10px]">manual</Badge>}</TableCell>
                      <TableCell className="text-sm">{fmt(e.clock_in)}</TableCell>
                      <TableCell className="text-sm">{fmt(e.clock_out)}</TableCell>
                      <TableCell className="text-sm">{hours(e.clock_in, e.clock_out)}</TableCell>
                      <TableCell><StatusBadge s={e.status} /></TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" className="rounded-full" onClick={() => openEdit(e)} data-testid={`log-edit-${e.id}`}><Pencil className="mr-1 h-3 w-3" /> Edit</Button>
                          <DeleteButton onConfirm={() => delEntry(e.id)} label="this time log" testId={`log-delete-${e.id}`} />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <div className="mt-8">
        <h2 className="font-heading text-lg font-bold">My time entries</h2>
        <div className="mt-3 overflow-hidden rounded-xl border border-border/60 bg-card">
          <Table>
            <TableHeader><TableRow><TableHead>Clock In</TableHead><TableHead>Clock Out</TableHead><TableHead>Hours</TableHead><TableHead className="text-right">Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {mine.length === 0 ? <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground">No time entries yet.</TableCell></TableRow>
                : mine.map((e) => (
                  <TableRow key={e.id} data-testid={`entry-row-${e.id}`}>
                    <TableCell className="text-sm">{fmt(e.clock_in)}</TableCell>
                    <TableCell className="text-sm">{fmt(e.clock_out)}</TableCell>
                    <TableCell className="text-sm">{hours(e.clock_in, e.clock_out)}</TableCell>
                    <TableCell className="text-right"><StatusBadge s={e.status} /></TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={!!editEntry} onOpenChange={(o) => !o && setEditEntry(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit Times</DialogTitle>
            <DialogDescription>Adjust {editEntry?.worker_name}'s clock-in/out. Approved entries update the Pay Preview automatically.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">Clock In</Label><Input type="datetime-local" data-testid="edit-clock-in" value={editForm.clock_in} onChange={(e) => setEditForm({ ...editForm, clock_in: e.target.value })} required /></div>
            <div className="space-y-1.5"><Label className="text-xs">Clock Out</Label><Input type="datetime-local" data-testid="edit-clock-out" value={editForm.clock_out} onChange={(e) => setEditForm({ ...editForm, clock_out: e.target.value })} /></div>
            {editInvalid && <p className="text-xs font-medium text-destructive" data-testid="edit-invalid-warning">Clock-out is before clock-in — fix it so the shift counts toward pay.</p>}
            <Button type="submit" disabled={editInvalid} className="w-full rounded-full" data-testid="edit-times-submit"><Check className="mr-1 h-4 w-4" /> Save Times</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Time Log</DialogTitle>
            <DialogDescription>Manually log a shift for a worker (e.g. they forgot to clock in). Approved logs count toward pay.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitAdd} className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Worker</Label>
              <Select value={addForm.worker_id} onValueChange={(v) => setAddForm({ ...addForm, worker_id: v })}>
                <SelectTrigger data-testid="add-log-worker"><SelectValue placeholder="Select worker" /></SelectTrigger>
                <SelectContent>
                  {workers.length === 0 ? <div className="px-3 py-2 text-sm text-muted-foreground">No workers — add them under Staff.</div>
                    : workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Clock In</Label><Input type="datetime-local" data-testid="add-log-clock-in" value={addForm.clock_in} onChange={(e) => setAddForm({ ...addForm, clock_in: e.target.value })} required /></div>
            <div className="space-y-1.5"><Label className="text-xs">Clock Out</Label><Input type="datetime-local" data-testid="add-log-clock-out" value={addForm.clock_out} onChange={(e) => setAddForm({ ...addForm, clock_out: e.target.value })} /></div>
            {addInvalid && <p className="text-xs font-medium text-destructive" data-testid="add-invalid-warning">Clock-out is before clock-in.</p>}
            <div className="space-y-1.5">
              <Label className="text-xs">Status</Label>
              <Select value={addForm.status} onValueChange={(v) => setAddForm({ ...addForm, status: v })}>
                <SelectTrigger data-testid="add-log-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="approved">Approved (counts toward pay)</SelectItem>
                  <SelectItem value="pending">Pending approval</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={addInvalid} className="w-full rounded-full" data-testid="add-log-submit"><Plus className="mr-1 h-4 w-4" /> Add Log</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
